import { useEffect, useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import axios from 'axios'
import dayjs from 'dayjs'
import { toast } from 'react-hot-toast'

import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material'

import PageAppBar from 'components/PageAppBar'
import ActivityHistoryDataGrid from 'components/ActivityHistoryDataGrid'
import {
  ProfileModel,
  ActivityModel,
  ProfileBadgeResponse,
  ProfileSurveyModel,
} from 'types'
import { base_url } from 'env'
import { WorkspacePremium, SmartToy } from '@mui/icons-material'

export default function ProfilePreview() {
  const navigate = useNavigate()
  const initialProfile = useLoaderData() as ProfileModel

  const [profile, setProfile] = useState<ProfileModel>(initialProfile)
  const [survey, setSurvey] = useState<ProfileSurveyModel | null>(null)
  const [suggestedActivityDetails, setSuggestedActivityDetails] =
    useState<ActivityModel | null>(null)
  const [unlockedBadges, setUnlockedBadges] = useState<ProfileBadgeResponse[]>(
    []
  )
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [isSuggesting, setIsSuggesting] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return

    const fetchProfileData = async () => {
      setIsLoadingData(true)
      try {
        const [activitiesRes, badgesRes, surveyRes] = await Promise.allSettled([
          axios.get(`${base_url}/activity/`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${base_url}/profile/${profile.id}/badge`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${base_url}/profile/${profile.id}/survey`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ])

        if (activitiesRes.status === 'fulfilled') {
          const allActivities: ActivityModel[] = activitiesRes.value.data
          const activityMap = new Map(
            allActivities.map((act) => [act.id!, act])
          )

          if (profile.suggestedActivity) {
            setSuggestedActivityDetails(
              activityMap.get(profile.suggestedActivity) || null
            )
          } else {
            setSuggestedActivityDetails(null)
          }
        } else {
          console.error('Failed to load activities:', activitiesRes.reason)
        }

        if (badgesRes.status === 'fulfilled') {
          setUnlockedBadges(badgesRes.value.data)
        }

        if (surveyRes.status === 'fulfilled') {
          setSurvey(surveyRes.value.data)
        }
      } catch (error) {
        console.error(
          'Erreur lors du chargement des données du profil :',
          error
        )
      } finally {
        setIsLoadingData(false)
      }
    }

    fetchProfileData()
  }, [profile.id, profile.suggestedActivity])

  const handleGetSuggestion = async () => {
    setIsSuggesting(true)
    const token = localStorage.getItem('token')
    try {
      await axios.get(`${base_url}/profile/${profile.id}/suggestion`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const updatedProfileRes = await axios.get(
        `${base_url}/profile/${profile.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setProfile(updatedProfileRes.data)
      alert('Nouvelle suggestion obtenue !')
    } catch (error) {
      console.error("Échec de l'obtention d'une nouvelle suggestion:", error)
      alert("Impossible d'obtenir une nouvelle suggestion pour le moment.")
    } finally {
      setIsSuggesting(false)
    }
  }

  const handleRevokeBadge = (badgeId: string) => {
    if (
      !window.confirm(`Êtes-vous sûr de vouloir retirer ce badge du profil ?`)
    )
      return

    const token = localStorage.getItem('token')
    axios
      .delete(`${base_url}/profile/${profile.id}/badge/${badgeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        alert('Badge retiré avec succès.')
        setUnlockedBadges(unlockedBadges.filter((b) => b.id !== badgeId))
      })
      .catch((err) => {
        console.error('Erreur lors de la suppression du badge:', err)
        alert('La suppression a échoué.')
      })
  }

  const handleDeleteSurvey = () => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer le questionnaire de ce profil ?`
      )
    )
      return

    axios
      .delete(`${base_url}/profile/${profile.id}/survey`)
      .then(() => {
        toast.success('Questionnaire supprimé avec succès.')
        setSurvey(null)
      })
      .catch((err) => {
        console.error('La suppression du questionnaire a échoué:', err)
      })
  }

  const renderUnlockedBadges = () => {
    return (
      <Card sx={{ width: '100%', maxWidth: '800px' }}>
        <CardContent>
          <Typography variant="h5" component="div" gutterBottom>
            Badges Débloqués
          </Typography>
          {unlockedBadges.length > 0 ? (
            <List dense>
              {unlockedBadges.map((badge) => (
                <ListItem
                  key={badge.id}
                  secondaryAction={
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRevokeBadge(badge.id!)}
                    >
                      Retirer
                    </Button>
                  }
                >
                  <ListItemIcon>
                    <WorkspacePremium color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={badge.name}
                    secondary={`Obtenu le: ${dayjs(
                      badge.unlockedDate * 1000
                    ).format('DD/MM/YYYY')}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">
              Aucun badge débloqué pour le moment.
            </Typography>
          )}
        </CardContent>
        <CardActions>
          <Button
            size="small"
            onClick={() => navigate(`/profile/${profile.id}/badge/assign`)}
          >
            Assigner un Badge
          </Button>
        </CardActions>
      </Card>
    )
  }

  const renderProfileSurvey = () => {
    if (!survey) {
      return (
        <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
          <CardContent>
            <Typography>Ce profil n'a pas encore de questionnaire.</Typography>
          </CardContent>
          <CardActions>
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/profile/${profile.id}/survey/create`)}
            >
              Créer un questionnaire
            </Button>
          </CardActions>
        </Card>
      )
    }
    return (
      <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Questionnaire du Profil
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Chip
                label={`Activités faites par: ${survey.relativeDoingActivities}`}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                label={`Parle une autre langue: ${
                  survey.canSpeakAnotherLangage ? 'Oui' : 'Non'
                }`}
                sx={{ width: '100%' }}
              />
            </Grid>
            {survey.canSpeakAnotherLangage && (
              <Grid item xs={12} sm={6}>
                <Chip
                  label={`Niveau autre langue: ${survey.levelInTheOtherLanguage}/5`}
                  sx={{ width: '100%' }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Chip
                label={`Lit ${survey.howOftenProfileRead} jours/semaine`}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Chip
                label={`Lit ${survey.timeProfileSpentReading} min/jour`}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Supports possédés :
              </Typography>
              <Chip
                label="Albums"
                disabled={!survey.hasPictureBook}
                sx={{ mr: 1 }}
              />
              <Chip
                label="BDs"
                disabled={!survey.hasComicBooks}
                sx={{ mr: 1 }}
              />
              <Chip
                label="Romans"
                disabled={!survey.hasYouthNovel}
                sx={{ mr: 1 }}
              />
              <Chip label="Magazines" disabled={!survey.hasYouthMagazine} />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions>
          <Button
            size="small"
            onClick={() => navigate(`/profile/${profile.id}/survey/edit`)}
          >
            Modifier
          </Button>
          <Button size="small" color="error" onClick={handleDeleteSurvey}>
            Supprimer
          </Button>
        </CardActions>
      </Card>
    )
  }

  if (isLoadingData) {
    return (
      <PageAppBar pageName="Chargement du profil...">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
          }}
        >
          <CircularProgress />
        </Box>
      </PageAppBar>
    )
  }

  return (
    <PageAppBar pageName={`Profil de ${profile.firstName}`}>
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {profile.firstName} {profile.lastName}
          </Typography>
          <Chip
            label={profile.title || 'Apprenti Lecteur'}
            color="secondary"
            sx={{ mr: 1 }}
          />
          <Chip label={`Classe: ${profile.grade || 'N/A'}`} color="primary" sx={{ mr: 1 }} />
          {profile.accountState === 'archived' && (
            <Chip label="Archivé" color="warning" />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
            justifyContent: 'center',
          }}
        >
          {profile.accountState === 'archived' ? (
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                if (!window.confirm('Restaurer ce profil ?')) return
                axios
                  .post(`${base_url}/profile/${profile.id}/restore`)
                  .then((res) => {
                    toast.success('Profil restauré avec succès.')
                    setProfile(res.data)
                  })
                  .catch((err) => {
                    console.error('Failed to restore profile:', err)
                  })
              }}
            >
              Restaurer le Profil
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={() => navigate(`/profile/${profile.id}/edit`)}
              >
                Modifier le Profil
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate(`/profile/${profile.id}/activity/create`)}
              >
                Enregistrer une Activité
              </Button>
              <Button
                variant="outlined"
                color="info"
                startIcon={<SmartToy />}
                onClick={() => navigate(`/profile/${profile.id}/recommender`)}
              >
                Test Algo
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => {
                  if (!window.confirm('Archiver ce profil ?')) return
                  const token = localStorage.getItem('token')
                  axios
                    .delete(`${base_url}/profile/${profile.id}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    })
                    .then((res) => {
                      toast.success('Profil archivé.')
                      setProfile(res.data)
                    })
                    .catch((err) => {
                      console.error("Échec de l'archivage:", err)
                      toast.error("Impossible d'archiver ce profil.")
                    })
                }}
              >
                Archiver
              </Button>
            </>
          )}
        </Box>

        <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Détails du Profil
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Chip
                  label={`ID: ${profile.id}`}
                  variant="outlined"
                  sx={{ width: '100%', justifyContent: 'flex-start', p: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={`ID Questionnaire: ${profile.surveyId || 'N/A'}`}
                  variant="outlined"
                  sx={{ width: '100%', justifyContent: 'flex-start', p: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={`Créé par: ${profile.createdBy}`}
                  variant="outlined"
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    p: 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/user/${profile.createdBy}`)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={`Objectif: ${profile.timeTarget || 'N/A'} min`}
                  variant="outlined"
                  sx={{ width: '100%', justifyContent: 'flex-start', p: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={`Naissance: ${dayjs(profile.birthday * 1000).format(
                    'DD/MM/YYYY'
                  )}`}
                  variant="outlined"
                  sx={{ width: '100%', justifyContent: 'flex-start', p: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={`Création: ${dayjs(profile.creationDate * 1000).format(
                    'DD/MM/YYYY HH:mm'
                  )}`}
                  variant="outlined"
                  sx={{ width: '100%', justifyContent: 'flex-start', p: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={profile.active ? 'Profil Actif' : 'Profil Inactif'}
                  color={profile.active ? 'success' : 'default'}
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  label={
                    profile.testLocked
                      ? 'Tests Verrouillés'
                      : 'Tests Déverrouillés'
                  }
                  color={profile.testLocked ? 'error' : 'success'}
                  sx={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {profile.activityTimes && (
          <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Temps par Catégorie (minutes)
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={3}>
                  <Chip
                    label={`Ludique: ${profile.activityTimes.ludique || 0}`}
                    color="warning"
                    sx={{ width: '100%' }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Chip
                    label={`Autonomie: ${profile.activityTimes.auto || 0}`}
                    color="secondary"
                    sx={{ width: '100%' }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Chip
                    label={`Renforcement: ${profile.activityTimes.renf || 0}`}
                    color="success"
                    sx={{ width: '100%' }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Chip
                    label={`Partage: ${profile.activityTimes.partag || 0}`}
                    color="primary"
                    sx={{ width: '100%' }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {renderUnlockedBadges()}
        {renderProfileSurvey()}

        <Card sx={{ width: '100%', maxWidth: '800px' }}>
          <CardContent>
            <Typography variant="h5" component="div">
              Activité Suggérée
            </Typography>
            {suggestedActivityDetails ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">
                  {suggestedActivityDetails.title}
                </Typography>
                <Typography color="text.secondary">
                  Catégorie : {suggestedActivityDetails.category} | Difficulté :{' '}
                  {suggestedActivityDetails.baseDifficulty}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {suggestedActivityDetails.instructions}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ mt: 2 }} color="text.secondary">
                Aucune activité n'est actuellement suggérée pour ce profil.
              </Typography>
            )}
          </CardContent>
          <CardActions>
            <Button
              size="small"
              onClick={handleGetSuggestion}
              disabled={isSuggesting}
            >
              {isSuggesting ? (
                <CircularProgress size={24} />
              ) : (
                'Obtenir une nouvelle suggestion'
              )}
            </Button>
          </CardActions>
        </Card>

        <Box sx={{ width: '100%', maxWidth: '1200px' }}>
          <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
            Historique des Activités
          </Typography>
          <ActivityHistoryDataGrid profileId={profile.id} />
        </Box>
      </Box>
    </PageAppBar>
  )
}
